import { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { AlertTriangle, Shield, Lock, Key } from 'lucide-react';
import { SimpleGraphNode, SimpleNodeData } from './types';

const borderColors: Record<string, string> = {
  anomaly: '#ef4444',
  role: '#06b6d4',
  secret: '#10b981',
  key: '#f97316',
};

export const SimpleCircularNode = memo(({ data, selected }: NodeProps<SimpleGraphNode>) => {
  const nodeData = data as SimpleNodeData;
  const borderColor = borderColors[nodeData.type] || '#6b7280';
  const isAnomaly = nodeData.type === 'anomaly';
  const isSecret = nodeData.type === 'secret';
  const isKey = nodeData.type === 'key';
  const bgColor = isAnomaly ? '#ef4444' : 'hsl(var(--background))';
  const iconColor = isAnomaly ? '#ffffff' : 'hsl(var(--foreground))';

  const nodeSize = 70;

  const getIcon = () => {
    if (isAnomaly) return <AlertTriangle className="w-6 h-6" style={{ color: iconColor }} />;
    if (isSecret) return <Lock className="w-6 h-6" style={{ color: iconColor }} />;
    if (isKey) return <Key className="w-6 h-6" style={{ color: iconColor }} />;
    return <Shield className="w-6 h-6" style={{ color: iconColor }} />;
  };

  return (
    <div className="relative flex flex-col items-center cursor-pointer transition-transform duration-200">
      {/* Main circle */}
      <div
        className={`rounded-full border-2 flex items-center justify-center transition-all ${
          selected ? 'ring-2 ring-sky-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]' : ''
        }`}
        style={{
          width: nodeSize,
          height: nodeSize,
          backgroundColor: bgColor,
          borderColor,
        }}
      >
        {/* Invisible handles */}
        <Handle
          type="target"
          position={Position.Left}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ left: '50%', top: '50%' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!opacity-0 !w-0 !h-0 !border-0"
          style={{ right: '50%', top: '50%' }}
        />

        {getIcon()}
      </div>

      {/* Label below */}
      <div className="mt-1 max-w-[100px] text-center">
        <span className="text-[11px] font-medium text-foreground block truncate">
          {nodeData.label}
        </span>
        <span className="text-[10px] text-muted-foreground uppercase">
          {nodeData.type.replace('_', ' ')}
        </span>
      </div>
    </div>
  );
});

SimpleCircularNode.displayName = 'SimpleCircularNode';
